import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/shared/ui/EmptyState';

/**
 * Local mirror of `OpportunitiesEmptyState` / `FavoritesEmptyState` from
 * `@/features/dashboard`.
 *
 * Why mirrored: each component is ~10 LOC of static copy wrapping the
 * shared `EmptyState` primitive. Per Q6, single-consumer + tiny LOC →
 * local mirror is cleaner than promoting to `shared/` or extending a
 * binding seam. Keeps `features/social-media-opportunities-widget`
 * Cross-feature-OUT clean without speculative shared promotion.
 *
 * Reciprocal-change rule: if `features/dashboard`'s versions ever change
 * copy or behavior, this mirror must move with them. The dashboard
 * originals remain in place (used by `RecentOpportunities`).
 */

interface OpportunitiesEmptyStateProps {
  height?: string | number;
}

export const OpportunitiesEmptyState = ({ height = '100%' }: OpportunitiesEmptyStateProps) => {
  const { t } = useTranslation('analysis');

  return (
    <EmptyState
      title={t('socialMediaOpportunitiesWidget.emptyStates.noOpportunityGeneratedRecently')}
      description={t('socialMediaOpportunitiesWidget.emptyStates.opportunitiesWillAppearHereAs')}
      data-testid="opportunities-empty-state"
      height={height}
    />
  );
};

export const FavoritesEmptyState = () => {
  const { t } = useTranslation('analysis');

  return (
    <EmptyState
      title={t('socialMediaOpportunitiesWidget.emptyStates.noFavoriteOpportunityAddedYet')}
      description={t('socialMediaOpportunitiesWidget.emptyStates.allYourFavoriteOpportunitiesWill')}
      data-testid="favorites-empty-state"
      height="100%"
    />
  );
};
